import { useState } from "react";
import { router } from "@inertiajs/react";
import { FileText, Wand2, Search } from "lucide-react";
import { MatchingJob } from "./_types";
import { Button } from "../../components/ui/button";
import AskPermissionButton from "../../components/AskPermissionButton";

interface Props {
  candidateId: number;
  candidateFirstName: string;
  candidateEmail: string;
  matchingJobs: MatchingJob[];
  aiAssist: boolean;
  practiceAreaOptions: string[];
  locations: string[];
  onLocationsChange: (v: string[]) => void;
  practiceAreas: string[];
  onPracticeAreasChange: (v: string[]) => void;
  includeRemote: boolean;
  onIncludeRemoteChange: (v: boolean) => void;
  onFindMatches: () => void;
  matching: boolean;
}

// Legacy high/medium fold onto the same strong/possible buckets as the new
// tiers, same convention as JobAlerts/Show's TIER_CONFIG.
const TIER_CONFIG: Record<string, { label: string; className: string }> = {
  strong:   { label: "Strong",   className: "bg-green-100 text-green-700" },
  high:     { label: "Strong",   className: "bg-green-100 text-green-700" },
  possible: { label: "Possible", className: "bg-amber-100 text-amber-700" },
  medium:   { label: "Possible", className: "bg-amber-100 text-amber-700" },
};

// A job's firm_name is "Unknown" (or blank) when Leopard couldn't parse a firm
// off the alert email (leopard_email_processor.rb). Don't render that as the
// bold heading — lead with the role title and mark the firm as unknown.
function firmKnown(job: MatchingJob): boolean {
  return !!job.firm_name && job.firm_name !== "Unknown";
}

export default function CoverLettersTab({
  candidateId,
  candidateFirstName,
  candidateEmail,
  matchingJobs,
  aiAssist,
  practiceAreaOptions,
  locations,
  onLocationsChange,
  practiceAreas,
  onPracticeAreasChange,
  includeRemote,
  onIncludeRemoteChange,
  onFindMatches,
  matching,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [generating, setGenerating]   = useState(false);

  function toggleJob(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds(selectedIds.size === matchingJobs.length ? new Set() : new Set(matchingJobs.map((j) => j.id)));
  }

  function togglePracticeArea(pa: string) {
    onPracticeAreasChange(
      practiceAreas.includes(pa) ? practiceAreas.filter((p) => p !== pa) : [...practiceAreas, pa]
    );
  }

  function generate() {
    if (selectedIds.size === 0) return;
    setGenerating(true);
    router.post(
      `/candidates/${candidateId}/batch_generate_cover_letters`,
      { job_ids: Array.from(selectedIds) },
      { onFinish: () => setGenerating(false) }
    );
  }

  if (!aiAssist) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Wand2 className="w-8 h-8 text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm">AI Assist is disabled.</p>
        <p className="text-gray-400 text-xs mt-1">Enable it in Settings to generate cover letters.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5">
      {/* Search filters (task ae6399ac) — hard-gate the reverse match by
          location + practice area so results stay in-market. */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-800">Search filters</h2>
          <Button onClick={onFindMatches} disabled={matching} size="sm" variant="outline">
            <Search className="w-3.5 h-3.5 mr-1.5" />
            {matching ? "Matching..." : "Find Matching Roles"}
          </Button>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Locations (one market per line)</label>
          <textarea
            value={locations.join("\n")}
            onChange={(e) => onLocationsChange(e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))}
            rows={2}
            placeholder="New York, NY"
            className="w-full text-sm rounded-md border border-gray-300 px-2.5 py-1.5 focus:border-purple-400 focus:ring-purple-400"
          />
          <p className="text-xs text-gray-400 mt-1">Only roles in these markets are matched. Leave empty to search everywhere.</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">Practice areas</label>
          <div className="flex flex-wrap gap-1.5">
            {practiceAreaOptions.map((pa) => {
              const on = practiceAreas.includes(pa);
              return (
                <button
                  key={pa}
                  type="button"
                  onClick={() => togglePracticeArea(pa)}
                  className={`text-xs px-2 py-1 rounded border transition-colors ${
                    on
                      ? "bg-purple-100 border-purple-300 text-purple-800"
                      : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {pa}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-1">Leave none selected to match any practice area.</p>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeRemote}
            onChange={(e) => onIncludeRemoteChange(e.target.checked)}
            className="rounded border-gray-300 text-purple-600"
          />
          Include remote roles
        </label>
      </div>

      {matchingJobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="w-8 h-8 text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No open jobs match this candidate yet.</p>
          <p className="text-gray-400 text-xs mt-1">Set your filters above and click "Find Matching Roles" to match now. Weak-tier matches aren't shown here.</p>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Matching Jobs</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Open jobs this candidate matches (Strong/Possible) — select to generate cover letters.
              </p>
            </div>
            <Button
              onClick={generate}
              disabled={generating || selectedIds.size === 0}
              className="bg-purple-700 hover:bg-purple-800 text-white shrink-0"
              size="sm"
            >
              <Wand2 className="w-3.5 h-3.5 mr-1.5" />
              {generating ? "Generating..." : `Generate${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
            </Button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedIds.size === matchingJobs.length && matchingJobs.length > 0}
                onChange={toggleAll}
                className="rounded border-gray-300 text-purple-600"
              />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {selectedIds.size > 0 ? `${selectedIds.size} of ${matchingJobs.length} selected` : `${matchingJobs.length} jobs`}
              </span>
            </div>

            <div className="divide-y divide-gray-50">
              {matchingJobs.map((job) => (
                <label
                  key={job.id}
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(job.id)}
                    onChange={() => toggleJob(job.id)}
                    className="mt-0.5 rounded border-gray-300 text-purple-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">
                        {firmKnown(job) ? job.firm_name : (job.title || "Untitled role")}
                      </p>
                      {!firmKnown(job) && (
                        <span className="text-xs text-gray-400 italic">Unknown firm</span>
                      )}
                      {job.tier && TIER_CONFIG[job.tier] && (
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${TIER_CONFIG[job.tier].className}`}>
                          {TIER_CONFIG[job.tier].label}
                        </span>
                      )}
                    </div>
                    {/* Only show the title as a subline when the firm is the
                        heading — otherwise it's already the heading above. */}
                    {firmKnown(job) && job.title && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{job.title}</p>
                    )}
                    {job.location && (
                      <p className="text-xs text-gray-400 mt-0.5">{job.location}</p>
                    )}
                    {/* React default-escaping, no dangerouslySetInnerHTML — same
                        convention as JobAlerts/Show for AI-derived rationale text. */}
                    {job.rationale && (
                      <p className="text-xs text-gray-400 italic mt-0.5 truncate">{job.rationale}</p>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5">
                    {job.practice_area}
                  </span>
                  {/* Ask-permission opens a modal; wrap so the click doesn't
                      toggle the row's selection checkbox (label default). */}
                  {candidateEmail && (
                    <span className="shrink-0" onClick={(e) => e.preventDefault()}>
                      <AskPermissionButton
                        candidate={{ first_name: candidateFirstName, email: candidateEmail }}
                        job={{ firm_name: job.firm_name, title: job.title, practice_area: job.practice_area }}
                        aiAssist={aiAssist}
                      />
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
